import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  Globe,
  Trash2,
  Ban,
  Search,
  Calendar,
  RefreshCw,
  ArrowRight,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronUp,
  Home,
  Shield,
  XCircle,
  BarChart3,
  Filter,
  Loader2,
  UserX
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import AppHeader from './AppHeader';
import { ConfirmationModal } from './ConfirmationModal';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Comment {
  id: number;
  session_id: string;
  session_title: string;
  author_name: string;
  content: string;
  timestamp_seconds: number;
  ip_address: string | null;
  is_pinned: boolean;
  is_anonymous: boolean;
  created_at: string;
}

interface CommentGroup {
  key: string;
  type: 'username' | 'ip';
  value: string;
  count: number;
  comments: Comment[];
  sessions: Set<string>;
  latestComment: string;
  expanded: boolean;
}

interface DailyStats {
  date: string;
  count: number;
}

interface BannedIP {
  id: number;
  ip_address: string;
  reason: string | null;
  banned_by: string | null;
  created_at: string;
}

type GroupByType = 'username' | 'ip' | 'none';
type TimeFilter = '7d' | '30d' | '90d' | 'all';

const CommentsExplorer: React.FC = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  const [comments, setComments] = useState<Comment[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [bannedIPs, setBannedIPs] = useState<BannedIP[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState<GroupByType>('username');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30d');
  const [showBannedIPs, setShowBannedIPs] = useState(false);

  // Modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banUserModalOpen, setBanUserModalOpen] = useState(false);
  const [unbanModalOpen, setUnbanModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [selectedIP, setSelectedIP] = useState<string | null>(null);
  const [selectedAuthor, setSelectedAuthor] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);

  // Expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const loadComments = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (timeFilter !== 'all') params.append('days', timeFilter.replace('d', ''));

      const response = await fetch(`${API_BASE_URL}/api/admin/comments?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load comments');
      const data = await response.json();
      setComments(data.comments);
    } catch (err) {
      setError('Failed to load comments');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [token, searchTerm, timeFilter]);

  const loadStats = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoadingStats(true);

      const params = new URLSearchParams();
      if (timeFilter !== 'all') params.append('days', timeFilter.replace('d', ''));

      const response = await fetch(`${API_BASE_URL}/api/admin/comments-stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load stats');
      const data = await response.json();
      setDailyStats(data.dailyStats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  }, [token, timeFilter]);

  const loadBannedIPs = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/admin/banned-ips', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load banned IPs');
      const data = await response.json();
      setBannedIPs(data.bannedIPs);
    } catch (err) {
      console.error('Failed to load banned IPs:', err);
    }
  }, [token]);

  useEffect(() => {
    loadComments();
    loadStats();
    loadBannedIPs();
  }, [loadComments, loadStats, loadBannedIPs]);

  // Group comments
  const groupedComments = useMemo(() => {
    if (groupBy === 'none') return null;

    const groups = new Map<string, CommentGroup>();

    comments.forEach(comment => {
      let key: string;
      let value: string;

      if (groupBy === 'username') {
        key = `user_${comment.author_name}`;
        value = comment.author_name;
      } else {
        key = `ip_${comment.ip_address || 'unknown'}`;
        value = comment.ip_address || 'Unknown IP';
      }

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          type: groupBy,
          value,
          count: 0,
          comments: [],
          sessions: new Set(),
          latestComment: comment.created_at,
          expanded: expandedGroups.has(key)
        });
      }

      const group = groups.get(key)!;
      group.count++;
      group.comments.push(comment);
      group.sessions.add(comment.session_id);
      if (new Date(comment.created_at) > new Date(group.latestComment)) {
        group.latestComment = comment.created_at;
      }
    });

    return Array.from(groups.values())
      .sort((a, b) => b.count - a.count);
  }, [comments, groupBy, expandedGroups]);

  const handleDeleteComment = async () => {
    if (!selectedComment || !token) return;

    try {
      setIsDeleting(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/comments/${selectedComment.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete comment');

      setComments(prev => prev.filter(c => c.id !== selectedComment.id));
      setDeleteModalOpen(false);
      setSelectedComment(null);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBanIP = async () => {
    if (!selectedIP || !token) return;

    try {
      setIsBanning(true);
      const response = await fetch('/api/admin/ban-ip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ip_address: selectedIP,
          reason: banReason || undefined
        })
      });

      if (!response.ok) throw new Error('Failed to ban IP');

      await loadBannedIPs();
      setBanModalOpen(false);
      setSelectedIP(null);
      setBanReason('');
    } catch (err) {
      console.error('Failed to ban IP:', err);
    } finally {
      setIsBanning(false);
    }
  };

  const handleUnbanIP = async () => {
    if (!selectedIP || !token) return;

    try {
      setIsBanning(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/banned-ips/${encodeURIComponent(selectedIP)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to unban IP');

      setBannedIPs(prev => prev.filter(b => b.ip_address !== selectedIP));
      setUnbanModalOpen(false);
      setSelectedIP(null);
    } catch (err) {
      console.error('Failed to unban IP:', err);
    } finally {
      setIsBanning(false);
    }
  };

  const handleBanUser = async () => {
    if (!selectedAuthor || !token) return;

    try {
      setIsBanning(true);
      const response = await fetch('/api/admin/ban-user', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authorName: selectedAuthor,
          reason: banReason || undefined
        })
      });

      if (!response.ok) throw new Error('Failed to ban user');

      // Remove all comments by this user from the local state
      setComments(prev => prev.filter(c => c.author_name !== selectedAuthor));
      await loadBannedIPs();
      setBanUserModalOpen(false);
      setSelectedAuthor(null);
      setBanReason('');
    } catch (err) {
      console.error('Failed to ban user:', err);
    } finally {
      setIsBanning(false);
    }
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatTimestamp = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isBannedIP = (ip: string | null) => {
    if (!ip) return false;
    return bannedIPs.some(b => b.ip_address === ip);
  };

  // Chart configuration
  const chartData = useMemo(() => ({
    labels: (dailyStats || []).map(s => {
      const date = new Date(s.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Comments',
        data: (dailyStats || []).map(s => s.count),
        fill: true,
        borderColor: isDarkMode ? 'rgb(147, 51, 234)' : 'rgb(124, 58, 237)',
        backgroundColor: isDarkMode ? 'rgba(147, 51, 234, 0.1)' : 'rgba(124, 58, 237, 0.1)',
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6
      }
    ]
  }), [dailyStats, isDarkMode]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: isDarkMode ? 'rgb(31, 41, 55)' : 'rgb(255, 255, 255)',
        titleColor: isDarkMode ? 'rgb(229, 231, 235)' : 'rgb(17, 24, 39)',
        bodyColor: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
        borderColor: isDarkMode ? 'rgb(55, 65, 81)' : 'rgb(229, 231, 235)',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: {
          color: isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.8)'
        },
        ticks: {
          color: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)'
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.8)'
        },
        ticks: {
          color: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
          stepSize: 1
        }
      }
    }
  };

  if (!token || !user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-600 dark:text-gray-400">Please log in to access this page.</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors">
      {/* Header */}
      <AppHeader
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        userName={user?.name}
        userEmail={user?.email}
        userPicture={user?.picture}
        onLogout={() => { void logout(); }}
        onHome={() => navigate('/')}
        token={token || undefined}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-gradient-to-br from-[#EEF1F3] to-[#FFF4D8] p-2 dark:from-[#232E3F] dark:to-[#332A17]">
                  <MessageSquare size={24} className="text-[#232E3F] dark:text-[#FFCF74]" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Comments Explorer</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Manage and monitor public comments across all sessions
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <Home size={18} />
                <span>Back to Home</span>
              </button>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
          >
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-[#EEF1F3] dark:bg-[#232E3F]/60 p-2">
                  <MessageSquare size={20} className="text-[#232E3F] dark:text-[#FFCF74]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{comments.length}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Comments</p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-[#FFF4D8] dark:bg-[#332A17] p-2">
                  <Users size={20} className="text-[#B57700] dark:text-[#FFE3A1]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {new Set(comments.map(c => c.author_name)).size}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Unique Users</p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-[#EEF1F3] dark:bg-[#232E3F]/60 p-2">
                  <Globe size={20} className="text-[#232E3F] dark:text-[#FFCF74]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {new Set(comments.map(c => c.ip_address).filter(Boolean)).size}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Unique IPs</p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
                  <Ban size={20} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{bannedIPs.length}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Banned IPs</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`p-6 rounded-xl border mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <BarChart3 size={20} className="text-[#B57700] dark:text-[#FFCF74]" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Comments Over Time</h2>
              </div>
              <div className="flex items-center space-x-2">
                {(['7d', '30d', '90d', 'all'] as TimeFilter[]).map(filter => (
                  <button
                    key={filter}
                    onClick={() => setTimeFilter(filter)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      timeFilter === filter
                        ? 'bg-[#232E3F] text-[#F4F4F4] dark:bg-[#1B2534] dark:text-[#FFE3A1]'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {filter === 'all' ? 'All Time' : filter}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-64">
              {isLoadingStats ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="animate-spin h-8 w-8 text-[#FFB52B]" />
                </div>
              ) : (dailyStats?.length || 0) > 0 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  No data available for the selected period
                </div>
              )}
            </div>
          </motion.div>

          {/* Filters and Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`p-4 rounded-xl border mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          >
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search comments, users, or sessions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B]`}
                />
              </div>

              {/* Group By */}
              <div className="flex items-center space-x-2">
                <Filter size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Group by:</span>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as GroupByType)}
                  className={`px-3 py-2 border rounded-lg ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="username">Username</option>
                  <option value="ip">IP Address</option>
                  <option value="none">No Grouping</option>
                </select>
              </div>

              {/* Refresh */}
              <button
                onClick={() => {
                  loadComments();
                  loadStats();
                }}
                disabled={isLoading}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>

              {/* Banned IPs Toggle */}
              <button
                onClick={() => setShowBannedIPs(!showBannedIPs)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  showBannedIPs
                    ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                    : isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <Shield size={16} />
                <span>Banned IPs ({bannedIPs.length})</span>
              </button>
            </div>
          </motion.div>

          {/* Banned IPs Panel */}
          <AnimatePresence>
            {showBannedIPs && bannedIPs.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`rounded-xl border mb-6 overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-2">
                    <Ban size={18} className="text-red-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Banned IP Addresses</h3>
                  </div>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {bannedIPs.map(ban => (
                    <div key={ban.id} className="p-4 flex items-center justify-between">
                      <div>
                        <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{ban.ip_address}</span>
                        {ban.reason && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{ban.reason}</p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Banned {formatTimeAgo(ban.created_at)}
                          {ban.banned_by && ` by ${ban.banned_by}`}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedIP(ban.ip_address);
                          setUnbanModalOpen(true);
                        }}
                        className="flex items-center space-x-1 rounded-lg px-3 py-1.5 text-sm text-[#B57700] transition-colors hover:bg-[#FFF4D8] dark:text-[#FFCF74] dark:hover:bg-[#332A17]/70"
                      >
                        <XCircle size={14} />
                        <span>Unban</span>
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Comments List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin h-8 w-8 text-[#FFB52B]" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                <p className="text-red-600 dark:text-red-400">{error}</p>
                <button
                  onClick={loadComments}
                  className="mt-4 px-4 py-2 text-[#B57700] hover:underline dark:text-[#FFCF74]"
                >
                  Try again
                </button>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No comments found</p>
              </div>
            ) : groupBy === 'none' ? (
              // Flat list of comments
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {comments.map(comment => (
                  <CommentRow
                    key={comment.id}
                    comment={comment}
                    isDarkMode={isDarkMode}
                    isBanned={isBannedIP(comment.ip_address)}
                    formatTimeAgo={formatTimeAgo}
                    formatTimestamp={formatTimestamp}
                    onDelete={() => {
                      setSelectedComment(comment);
                      setDeleteModalOpen(true);
                    }}
                    onBan={() => {
                      setSelectedIP(comment.ip_address);
                      setBanModalOpen(true);
                    }}
                    onBanUser={() => {
                      setSelectedAuthor(comment.author_name);
                      setBanUserModalOpen(true);
                    }}
                    onNavigate={() => navigate(`/session/${comment.session_id}`)}
                  />
                ))}
              </div>
            ) : (
              // Grouped comments
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {groupedComments?.map(group => (
                  <div key={group.key}>
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className={`w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`}
                    >
                      <div className="flex items-center space-x-3">
                        {group.type === 'username' ? (
                          <Users size={18} className="text-gray-400" />
                        ) : (
                          <Globe size={18} className="text-gray-400" />
                        )}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {group.value}
                        </span>
                        {group.type === 'ip' && isBannedIP(group.value !== 'Unknown IP' ? group.value : null) && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-full">
                            Banned
                          </span>
                        )}
                        <span className="rounded-full bg-[#EEF1F3] px-2 py-0.5 text-xs text-[#232E3F] dark:bg-[#232E3F] dark:text-[#FFE3A1]">
                          {group.count} comment{group.count !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          across {group.sessions.size} session{group.sessions.size !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Latest: {formatTimeAgo(group.latestComment)}
                        </span>
                        {expandedGroups.has(group.key) ? (
                          <ChevronUp size={18} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={18} className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    <AnimatePresence>
                      {expandedGroups.has(group.key) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className={`${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}
                        >
                          {group.comments.map(comment => (
                            <CommentRow
                              key={comment.id}
                              comment={comment}
                              isDarkMode={isDarkMode}
                              isBanned={isBannedIP(comment.ip_address)}
                              formatTimeAgo={formatTimeAgo}
                              formatTimestamp={formatTimestamp}
                              onDelete={() => {
                                setSelectedComment(comment);
                                setDeleteModalOpen(true);
                              }}
                              onBan={() => {
                                setSelectedIP(comment.ip_address);
                                setBanModalOpen(true);
                              }}
                              onBanUser={() => {
                                setSelectedAuthor(comment.author_name);
                                setBanUserModalOpen(true);
                              }}
                              onNavigate={() => navigate(`/session/${comment.session_id}`)}
                              isNested
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedComment(null);
        }}
        onConfirm={handleDeleteComment}
        title="Delete Comment"
        message={`Are you sure you want to delete this comment by "${selectedComment?.author_name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />

      {/* Ban IP Modal */}
      <ConfirmationModal
        isOpen={banModalOpen}
        onClose={() => {
          setBanModalOpen(false);
          setSelectedIP(null);
          setBanReason('');
        }}
        onConfirm={handleBanIP}
        title="Ban IP Address"
        message={
          <div className="space-y-4">
            <p>Are you sure you want to ban the IP address <strong className="font-mono">{selectedIP}</strong>?</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">This will prevent any future comments from this IP address.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="e.g., Spam, Abuse, etc."
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
          </div>
        }
        confirmText="Ban IP"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={isBanning}
      />

      {/* Unban IP Modal */}
      <ConfirmationModal
        isOpen={unbanModalOpen}
        onClose={() => {
          setUnbanModalOpen(false);
          setSelectedIP(null);
        }}
        onConfirm={handleUnbanIP}
        title="Unban IP Address"
        message={`Are you sure you want to unban the IP address ${selectedIP}? They will be able to post comments again.`}
        confirmText="Unban"
        cancelText="Cancel"
        confirmButtonClassName="bg-[#FFB52B] hover:bg-[#FFC54D] text-[#232E3F]"
        isLoading={isBanning}
      />

      {/* Ban User Modal */}
      <ConfirmationModal
        isOpen={banUserModalOpen}
        onClose={() => {
          setBanUserModalOpen(false);
          setSelectedAuthor(null);
          setBanReason('');
        }}
        onConfirm={handleBanUser}
        title="Ban User"
        message={
          <div className="space-y-4">
            <p>Are you sure you want to ban <strong>{selectedAuthor}</strong>?</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This will delete all their comments and ban their IP addresses, preventing future comments.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="e.g., Spam, Abuse, etc."
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
          </div>
        }
        confirmText="Ban User"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={isBanning}
      />
    </div>
  );
};

// Comment Row Component
interface CommentRowProps {
  comment: Comment;
  isDarkMode: boolean;
  isBanned: boolean;
  formatTimeAgo: (date: string) => string;
  formatTimestamp: (seconds: number) => string;
  onDelete: () => void;
  onBan: () => void;
  onBanUser: () => void;
  onNavigate: () => void;
  isNested?: boolean;
}

const CommentRow: React.FC<CommentRowProps> = ({
  comment,
  isDarkMode,
  isBanned,
  formatTimeAgo,
  formatTimestamp,
  onDelete,
  onBan,
  onBanUser,
  onNavigate,
  isNested = false
}) => {
  return (
    <div className={`p-4 ${isNested ? 'pl-12' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-gray-900 dark:text-gray-100">{comment.author_name}</span>
            {!!comment.is_pinned && (
              <span className="px-1.5 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 rounded">
                Pinned
              </span>
            )}
            {isBanned && (
              <span className="px-1.5 py-0.5 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded">
                Banned IP
              </span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatTimeAgo(comment.created_at)}
            </span>
            {comment.ip_address && (
              <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                {comment.ip_address}
              </span>
            )}
          </div>

          {/* Content */}
          <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{comment.content}</p>

          {/* Session Info */}
          <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
            <button
              onClick={onNavigate}
              className="flex items-center space-x-1 transition-colors hover:text-[#B57700] dark:hover:text-[#FFCF74]"
            >
              <ArrowRight size={12} />
              <span className="truncate max-w-[200px]">{comment.session_title}</span>
            </button>
            <span className="flex items-center space-x-1">
              <Clock size={12} />
              <span>@ {formatTimestamp(comment.timestamp_seconds)}</span>
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1 ml-4">
          {/* Only show ban options for anonymous users */}
          {!!comment.is_anonymous && (
            <>
              <button
                onClick={onBanUser}
                className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Ban user (delete all comments + ban IPs)"
              >
                <UserX size={14} />
              </button>
              {comment.ip_address && !isBanned && (
                <button
                  onClick={onBan}
                  className="p-1.5 text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                  title="Ban this IP only"
                >
                  <Ban size={14} />
                </button>
              )}
            </>
          )}
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Delete this comment"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentsExplorer;
